import React, { useCallback, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import {
  PLAN_ORDER,
  PlanNameKeys,
} from "../../../../../constants/subscription";
import PlanPriceTag from "../../../../ui/PlanPriceTag";
import { useDismissable } from "../hooks/useDismissable";
import { planLimitSummary } from "../utils/planSummary";

/**
 * "Current plan: Start · Free", and the list to change it from, on every step.
 *
 * Only the plans on sale are offered (PLAN_ORDER): Custom is quoted, not
 * chosen, and Business is retired. A company still on either sees its plan
 * named on the button and can move to one of these.
 *
 * Prices are the list's own currency (EUR), from the same price list the public
 * pricing page reads, so a promotion shows here exactly as it did there.
 */
const PlanMenu = ({ plan, quotes, onSelect, busy = false }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  const ref = useDismissable(open, close);
  const current = Number(plan);

  const choose = (value) => {
    setOpen(false);
    if (value !== current) onSelect(value);
  };

  return (
    <div ref={ref} className="relative min-w-0">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={busy}
        onClick={() => setOpen((value) => !value)}
        className="inline-flex min-h-11 max-w-full cursor-pointer items-center gap-2 rounded-lg border border-brand-600 bg-brand-50 px-3 py-2 text-sm font-semibold text-brand-800 transition hover:bg-brand-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600 disabled:cursor-wait disabled:opacity-70"
      >
        <i
          className={busy ? "bi bi-arrow-repeat animate-spin" : "bx bx-crown text-lg"}
          aria-hidden="true"
        />
        <span className="hidden sm:inline">
          {t("Dashboard.Setup.plan.current")}
        </span>
        <span className="min-w-0 truncate">
          {t(PlanNameKeys[current] ?? PlanNameKeys[PLAN_ORDER[0]])}
          {/* The price is in the menu too; on a phone the button has room for
              the name only. */}
          <span className="hidden sm:inline">
            {" · "}
            <PlanPriceTag quote={quotes[current]} size="sm" />
          </span>
        </span>
        <i className="bi bi-chevron-down text-xs" aria-hidden="true" />
      </button>

      {open && (
        <div
          role="menu"
          aria-label={t("Dashboard.Setup.plan.menuLabel")}
          className="absolute top-full left-0 z-40 mt-2 w-80 max-w-[calc(100vw-1.5rem)] rounded-card border border-ink-200 bg-white p-2 shadow-card-hover"
        >
          {PLAN_ORDER.map((value) => {
            const isCurrent = value === current;
            return (
              <button
                key={value}
                type="button"
                role="menuitemradio"
                aria-checked={isCurrent}
                onClick={() => choose(value)}
                className={`flex w-full cursor-pointer items-start gap-3 rounded-lg px-3 py-2.5 text-left transition focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-brand-600 ${
                  isCurrent ? "bg-brand-50" : "hover:bg-ink-50"
                }`}
              >
                <span
                  className={`mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full border-2 ${
                    isCurrent ? "border-brand-600 bg-brand-600" : "border-ink-300"
                  }`}
                  aria-hidden="true"
                >
                  {isCurrent && <i className="bi bi-check text-sm text-white" />}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex flex-wrap items-baseline justify-between gap-x-2">
                    <span className="font-semibold text-ink-900">
                      {t(PlanNameKeys[value])}
                    </span>
                    <PlanPriceTag
                      quote={quotes[value]}
                      size="sm"
                      className="text-sm font-semibold text-brand-700"
                    />
                  </span>
                  <span className="mt-0.5 block text-xs text-ink-500">
                    {planLimitSummary(value, quotes, t)}
                  </span>
                </span>
              </button>
            );
          })}
          <p className="m-0 mt-1 border-t border-ink-100 px-3 pt-2 pb-1 text-xs text-ink-500">
            {t("Dashboard.Setup.plan.changeAnytime")}
          </p>
        </div>
      )}
    </div>
  );
};

PlanMenu.propTypes = {
  plan: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  /** The price list by plan, from `pricingByPlan`. */
  quotes: PropTypes.object.isRequired,
  onSelect: PropTypes.func.isRequired,
  busy: PropTypes.bool,
};

export default PlanMenu;
